import { io, Socket } from "socket.io-client";
import type { TripOfferPayload } from "@/entities/trip";
import { env } from "../constants/env";
import { decodeJwtPayload } from "@/features/auth/config/keycloak";
import { refreshAccessTokens } from "@/features/auth/services/keycloakAuthService";
import { useSessionStore } from "../stores/sessionStore";
import { isUsableCoordinate } from "./coordinates";

// ═══════════════════════════════════════════════════════════════════════════
// Passerelle temps réel — conforme aux onze clauses du §4 du contrat
// d'intégration du 2026-08-26.
//
// Le constat qui a motivé ces clauses, mesuré en trois jours de production :
// 2 865 positions diffusées, ZÉRO demande d'entrée en salle, trois connexions
// abouties en douze heures. Le serveur émettait ; personne n'écoutait. La carte
// restait immobile non pas parce que la position manquait, mais parce qu'elle
// arrivait dans une pièce vide.
//
// Chaque clause correspond à une panne observée, pas à une préférence de style.
// Les références « R1 » … « R11 » ci-dessous renvoient à ce document.
// ═══════════════════════════════════════════════════════════════════════════

// ─── Événements serveur → client ──────────────────────────────────────────────

export type OrderUpdatedPayload = {
  order_id:   string;
  status:     string;
  updated_at: string;
};

export type AgentLocationUpdatedPayload = {
  agent_id:         string;
  latitude:         number;
  longitude:        number;
  accuracy_meters?: number;
  timestamp:        string;
};

/**
 * Événements portés par la salle d'une course.
 *
 * ⚠️ Liste OUVERTE, volontairement. Le nom d'un événement se déduit de son type
 * côté serveur (`TRIP_AGENT_ASSIGNED` → `trip.agent.assigned`) : un événement
 * nouveau apparaîtra donc sans que cette liste soit mise à jour. Le contrat le
 * dit explicitement — « ne construisez pas d'aiguillage exhaustif qui casserait
 * sur un nom inconnu ». D'où `subscribeTrip`, qui écoute AUSSI ce qu'il ne
 * connaît pas (cf. `onAnyEvent`).
 */
export const TRIP_ROOM_EVENTS = [
  // ─── Liste du §2 du contrat du 2026-08-31 ────────────────────────────────
  "trip.created",
  "trip.status.changed",
  "trip.agent.assigned",
  // ⚠️ `trip.agent.offered` N'EST PAS ICI, et ne doit jamais y revenir.
  //
  // C'est le SEUL événement diffusé vers une salle PERSONNELLE (`user:{sub}`).
  // L'écouter au titre d'une salle de course est d'ailleurs impossible : pour
  // entrer dans `trip:{id}`, il faut connaître l'identifiant de la course —
  // que seule l'offre nous apprendrait. Cf. `subscribeOffers`.
  "trip.agent.abandoned",
  "trip.stop.advanced",
  "trip.awaiting.client.approval",
  "trip.approval.expired",
  "trip.payment.settled",
  "trip.cancelled",
  "trip.rated",
  "trip.disputed",
  "trip.dispute.rejected",
  "trip.refunded",
  "trip.no.agent.available",
  "trip.wallet.insufficient",
  "trip.stop.skipped.all.unavailable",

  // ─── Noms hérités, conservés volontairement ───────────────────────────────
  // Le contrat du 2026-08-31 les a renommés, mais rien ne garantit qu'aucune
  // instance ne diffuse plus les anciens : entre deux déploiements, les deux
  // coexistent. Les retirer ferait manquer un changement d'état à un client
  // branché sur une instance pas encore à jour — un écran figé, sans erreur.
  // Écouter un nom que personne n'émet ne coûte rien ; en manquer un coûte une
  // course.
  "trip.updated",
  "trip.offer.expired",
  "trip.awaiting_approval",
  "trip.expired.no.agent",
] as const;

/**
 * Les deux noms sous lesquels arrive « le client doit trancher ».
 *
 * `trip.awaiting_approval` était le nom en vigueur au 2026-08-26 ; le contrat
 * du 2026-08-31 le normalise en `trip.awaiting.client.approval`, conformément à
 * la règle générale (`TRIP_AWAITING_CLIENT_APPROVAL`, minuscules, `_` → `.`).
 *
 * On accepte les deux : c'est cet événement qui porte le `photo_media_id` de la
 * preuve de retrait, et le manquer prive le client de la photo du colis sur
 * laquelle il doit justement s'engager.
 */
export const AWAITING_APPROVAL_EVENTS = [
  "trip.awaiting.client.approval",
  "trip.awaiting_approval",
] as const;

export type TripRoomEvent = (typeof TRIP_ROOM_EVENTS)[number];

/**
 * `trip.awaiting_approval` — la course attend une décision du client.
 *
 * `motif` distingue les deux origines depuis le 2026-08-26 :
 *   • `"retrait"` — LIVRAISON : la Main a photographié le colis au point de
 *     retrait. `photo_media_id` porte la photo à afficher, et il n'y a AUCUN
 *     article à approuver un par un.
 *   • absent / autre — COURSE : la liste d'articles vérifiés, comme avant.
 */
export type TripAwaitingApprovalPayload = {
  trip_id?:        string;
  stop_index?:     number;
  motif?:          string;
  photo_media_id?: string | null;
};

/**
 * `trip.cancelled` — qui a mis fin à la course.
 *
 * ⚠️ TROIS valeurs, pas deux. Outre `client` et `admin`, une tâche automatique
 * peut trancher : expiration du délai de validation, aucune Main trouvée,
 * plafond de sollicitations atteint. Un affichage qui n'en prévoirait que deux
 * tomberait sur une annulation système — le cas le moins visible en
 * développement, et le plus fréquent en production creuse.
 */
export type TripCancelledBy = "client" | "admin" | "systeme";

export type TripCancelledPayload = {
  trip_id?:      string;
  cancelled_by?: TripCancelledBy | string;
  reason?:       string | null;
};

export type RealtimeEvents = {
  "order.updated":          OrderUpdatedPayload;
  "agent.location.updated": AgentLocationUpdatedPayload;
};

/** Charge utile de `ready` (R4). */
export type ReadyPayload = {
  socket_id?: string;
  /** Le serveur a-t-il pu REJOUER la session ? Cf. R5 — tout en dépend. */
  recovered?: boolean;
  rooms?:     string[];
  a_faire?:   unknown;
};

/** Motifs de refus d'entrée en salle (R6). */
export type JoinRefusalReason = "non_autorise" | "indisponible" | "trip_id_manquant";

type JoinAck = { ok?: boolean; room?: string; raison?: JoinRefusalReason | string };

// ─── Singleton Socket.IO ──────────────────────────────────────────────────────
// Une seule connexion partagée par tous les hooks. Le jeton est relu à chaque
// (re)connexion → re-authentifié automatiquement après un rafraîchissement.

let socket: Socket | null = null;

// ─── Registre des salles ──────────────────────────────────────────────────────
// R6/R7 : on ne se contente plus de mémoriser les noms. Chaque salle porte son
// ÉTAT, parce que « demandée » et « obtenue » ne sont pas la même chose — et
// c'est précisément la confusion des deux qui laissait un client connecté,
// tous les voyants au vert, sans jamais rien recevoir.

type RoomKind = "trip" | "order" | "agent";

type RoomState = {
  kind:      RoomKind;
  id:        string;
  status:    "pending" | "joined" | "refused";
  /** Refus définitif : `non_autorise` ne se réessaie jamais. */
  reason?:   JoinRefusalReason | string;
  attempts:  number;
  retry?:    ReturnType<typeof setTimeout>;
  /**
   * Nombre d'abonnés vivants.
   *
   * Deux écrans peuvent suivre la MÊME course en même temps — la carte plein
   * écran et le panneau de la colonne de droite, par exemple. Sans ce compteur,
   * le démontage du premier quittait la salle et l'autre devenait muet, sans
   * qu'aucune erreur ne le signale.
   */
  refs:      number;
};

const rooms = new Map<string, RoomState>();

/**
 * Observateurs de l'ÉTAT d'une salle — pas seulement de ses refus.
 *
 * ## Le silence que ceci brise
 * `subscribeRoomRefused` existait déjà, et n'était consommé nulle part. Or
 * l'écran qui compte sur une salle a besoin de savoir si elle est RÉELLEMENT
 * acquise, et pas seulement si la socket est branchée :
 *
 *   • une entrée refusée (`non_autorise`) est DÉFINITIVE, sans nouvelle
 *     tentative ;
 *   • une entrée `indisponible` reste en attente, avec un délai croissant qui
 *     monte jusqu'à trente secondes ;
 *   • une reconnexion repasse toutes les salles acquises en `pending`.
 *
 * Dans ces trois cas la socket est « connectée » et pourtant AUCUN événement
 * n'arrive pour cette course. Un écran qui coupe son sondage sur la seule foi
 * de `getIsSocketConnected()` se fige alors sans le savoir — c'est ce qui
 * faisait qu'une Main ne voyait la validation du client qu'après avoir
 * rechargé sa page.
 */
type RoomStatusListener = (key: string, status: RoomState["status"]) => void;
const roomStatusListeners = new Set<RoomStatusListener>();

export function subscribeRoomStatus(listener: RoomStatusListener): () => void {
  roomStatusListeners.add(listener);
  return () => { roomStatusListeners.delete(listener); };
}

/** Pose l'état d'une salle ET prévient les écrans qui en dépendent. */
function setRoomStatus(key: string, status: RoomState["status"]): void {
  const state = rooms.get(key);
  if (!state || state.status === status) return;
  state.status = status;
  for (const fn of roomStatusListeners) fn(key, status);
}

/** La salle est-elle RÉELLEMENT acquise ? (et non « la socket répond ») */
export function isRoomJoined(kind: RoomKind, id: string | undefined): boolean {
  if (!id) return false;
  return rooms.get(`${kind}:${id}`)?.status === "joined";
}

const joinEventOf: Record<RoomKind, string> = {
  trip:  "join.trip",
  order: "join.order",
  agent: "join.agent",
};

const joinPayloadOf: Record<RoomKind, (id: string) => Record<string, string>> = {
  trip:  (id) => ({ trip_id: id }),
  order: (id) => ({ order_id: id }),
  agent: (id) => ({ agent_id: id }),
};

/** Retente une entrée refusée pour cause d'indisponibilité, avec délai croissant. */
const RETRY_BASE_MS = 1_500;
const RETRY_MAX_MS  = 30_000;

function retryDelay(attempts: number): number {
  return Math.min(RETRY_BASE_MS * 2 ** Math.max(0, attempts - 1), RETRY_MAX_MS);
}

// Observateurs des refus définitifs : un écran peut ainsi dire « suivi
// indisponible » au lieu d'attendre en silence des événements qui ne viendront
// jamais.
type RoomRefusedListener = (room: string, reason: JoinRefusalReason | string) => void;
const roomRefusedListeners = new Set<RoomRefusedListener>();

export function subscribeRoomRefused(listener: RoomRefusedListener): () => void {
  roomRefusedListeners.add(listener);
  return () => { roomRefusedListeners.delete(listener); };
}

/**
 * Demande l'entrée dans une salle ET LIT L'ACCUSÉ (R6).
 *
 * L'entrée en salle échouait autrefois en silence : un client refusé restait
 * connecté sans jamais rien recevoir, sans savoir pourquoi. L'accusé est
 * facultatif au sens de Socket.IO — un client qui n'en fournit pas continue de
 * fonctionner — mais on perd alors la seule information qui distingue « vous
 * n'y avez pas droit » de « réessayez ».
 */
function joinRoom(key: string): void {
  const s = socket;
  const state = rooms.get(key);
  if (!s || !state) return;

  if (state.retry) {
    clearTimeout(state.retry);
    state.retry = undefined;
  }
  state.attempts += 1;

  s.emit(joinEventOf[state.kind], joinPayloadOf[state.kind](state.id), (ack?: JoinAck) => {
    // Un serveur qui n'implémente pas l'ACK ne rappellera JAMAIS ce callback :
    // l'absence de réponse n'est donc pas un échec, et la salle reste
    // considérée comme demandée.
    if (!ack) return;
    // La salle a pu être quittée pendant l'aller-retour.
    const current = rooms.get(key);
    if (!current) return;

    if (ack.ok) {
      current.reason = undefined;
      setRoomStatus(key, "joined");
      return;
    }

    const raison = ack.raison ?? "indisponible";

    if (raison === "indisponible") {
      // Un service en aval ne répond pas : c'est temporaire, on réessaie avec
      // un délai croissant plutôt que de marteler la passerelle.
      current.reason = raison;
      setRoomStatus(key, "pending");
      const delay = retryDelay(current.attempts);
      if (env.isDev) {
        console.warn(`[realtime] ${key} indisponible — nouvelle tentative dans ${delay} ms`);
      }
      current.retry = setTimeout(() => joinRoom(key), delay);
      return;
    }

    // `non_autorise` (jeton, ou course qui n'est pas la vôtre) et
    // `trip_id_manquant` (appel incomplet — un bug de CE code) sont définitifs.
    // Réessayer ne ferait que consommer le quota de 240 requêtes par minute.
    current.reason = raison;
    setRoomStatus(key, "refused");
    if (env.isDev) {
      console.warn(`[realtime] entrée refusée dans ${key} : ${raison} — pas de nouvelle tentative`);
    }
    for (const fn of roomRefusedListeners) fn(key, raison);
  });
}

/**
 * Enregistre un abonné et dit s'il faut RÉELLEMENT demander l'entrée.
 *
 * Un second écran sur la même course ne redemande rien : la salle est déjà
 * demandée, ou déjà acquise. Émettre une seconde fois ne rapporterait aucune
 * salle de plus et consommerait le quota de 240 requêtes par minute — le
 * scénario est banal (le panneau de la colonne de droite et la carte plein
 * écran suivent la même course).
 *
 * Seule exception : une salle précédemment REFUSÉE. Une nouvelle demande
 * explicite de l'app signifie que quelque chose a changé — écran rouvert,
 * jeton rafraîchi — et mérite une tentative.
 */
function addRoom(kind: RoomKind, id: string): { key: string; shouldJoin: boolean } {
  const key = `${kind}:${id}`;
  const existing = rooms.get(key);
  if (!existing) {
    rooms.set(key, { kind, id, status: "pending", attempts: 0, refs: 1 });
    return { key, shouldJoin: true };
  }

  existing.refs += 1;
  if (existing.status === "refused") {
    existing.status = "pending";
    existing.attempts = 0;
    existing.reason = undefined;
    return { key, shouldJoin: true };
  }
  return { key, shouldJoin: false };
}

function removeRoom(key: string): void {
  const state = rooms.get(key);
  if (!state) return;
  state.refs -= 1;
  if (state.refs > 0) return;
  if (state.retry) clearTimeout(state.retry);
  rooms.delete(key);
}

/** Redemande TOUTES les salles suivies (après une reconnexion sans rejeu). */
function rejoinRooms(): void {
  for (const [key, state] of rooms) {
    // Un refus d'autorisation ne devient pas une autorisation en se reconnectant.
    if (state.status === "refused") continue;
    state.attempts = 0;
    joinRoom(key);
  }
}

// ─── Observers de connectivité ───────────────────────────────────────────────
// Les écrans live (tracking, mission) s'abonnent pour afficher un bandeau
// « Connexion perdue » et n'activer leur repli REST qu'à ce moment-là.

type ConnectionListener = (connected: boolean) => void;
const connectionListeners = new Set<ConnectionListener>();
let isConnected = false;

function notifyConnection(state: boolean) {
  if (isConnected === state) return;
  isConnected = state;
  for (const fn of connectionListeners) fn(state);
}

export function getIsSocketConnected(): boolean {
  return isConnected;
}

export function subscribeConnection(listener: ConnectionListener): () => void {
  connectionListeners.add(listener);
  // Notification initiale synchrone — l'écran sait immédiatement où il en est.
  listener(isConnected);
  return () => { connectionListeners.delete(listener); };
}

// ─── Jeton frais avant (re)connexion (R2) ────────────────────────────────────
// Marge avant expiration : en deçà, on rafraîchit plutôt que de tenter un
// handshake avec un jeton qui expirera pendant la poignée de main.
const TOKEN_MARGIN_MS = 30_000;

/**
 * Rafraîchit le jeton d'accès ET met la session à jour.
 *
 * ⚠️ `refreshAccessTokens` ne touche PAS au store — c'est l'intercepteur axios
 * qui le fait d'ordinaire. Rafraîchir ici sans enregistrer laissait le socket
 * repartir avec un jeton neuf pendant que les requêtes REST continuaient avec
 * l'ancien, jusqu'à ce qu'un 401 les rattrape une à une.
 */
async function refreshSession(): Promise<string | undefined> {
  const { refreshToken } = useSessionStore.getState();
  if (!refreshToken) return undefined;

  try {
    const res = await refreshAccessTokens(refreshToken);
    if (res.access_token) {
      useSessionStore.getState().updateTokens(
        res.access_token,
        res.refresh_token,
        Date.now() + res.expires_in * 1000,
        res.refresh_expires_in > 0 ? Date.now() + res.refresh_expires_in * 1000 : 0,
      );
    }
    return res.access_token || useSessionStore.getState().accessToken;
  } catch {
    return undefined;
  }
}

/**
 * Jeton d'accès valide, rafraîchi si nécessaire.
 *
 * `refreshAccessTokens` est un singleton : plusieurs reconnexions simultanées
 * (socket + requêtes REST) partagent le même appel réseau, jamais deux.
 *
 * En cas d'échec, on rend le jeton courant plutôt que rien : le serveur
 * tranchera. Bloquer la connexion ici priverait l'app de son temps réel pour un
 * refus qui n'est pas encore certain.
 */
async function freshAccessToken(force = false): Promise<string | undefined> {
  const { accessToken } = useSessionStore.getState();
  if (!accessToken) return undefined;

  if (!force) {
    let expiresAt: number | undefined;
    try {
      const exp = decodeJwtPayload(accessToken).exp;
      if (typeof exp === "number") expiresAt = exp * 1000;
    } catch {
      // Jeton illisible : on le laisse passer, le serveur le rejettera.
    }
    if (expiresAt == null || expiresAt - Date.now() > TOKEN_MARGIN_MS) return accessToken;
  }

  return (await refreshSession()) ?? useSessionStore.getState().accessToken;
}

// ─── Reprise après reconnexion (R4/R5) ───────────────────────────────────────
/** Délai au-delà duquel on rejoint les salles sans avoir reçu `ready`. */
const READY_GRACE_MS = 1_500;
let readyFallback: ReturnType<typeof setTimeout> | null = null;

// Écoutés par les hooks pour recharger leurs données quand la session n'a PAS
// été rejouée — les événements de la coupure sont alors définitivement perdus.
type ResyncListener = () => void;
const resyncListeners = new Set<ResyncListener>();

/**
 * S'abonne aux reprises de session non rejouées.
 *
 * L'appelant y remet ses données à jour depuis l'API. C'est le seul moyen de
 * rattraper ce qui a changé pendant la coupure : le socket, lui, ne rejouera
 * rien.
 */
export function subscribeResync(listener: ResyncListener): () => void {
  resyncListeners.add(listener);
  return () => { resyncListeners.delete(listener); };
}

// ─── Reprise après refus d'authentification (R2) ─────────────────────────────
/**
 * Nombre de `connect_error: unauthorized` CONSÉCUTIFS tolérés.
 *
 * Le contrat est net : « Rafraîchissez, puis reconnectez. **Ne bouclez pas** —
 * la limite de débit est à 240 requêtes par minute. » Avec
 * `reconnectionAttempts: Infinity`, un jeton révoqué produirait une tentative
 * toutes les 1 à 10 secondes, indéfiniment. On rafraîchit une fois, on laisse
 * une seconde chance, puis on s'arrête : la connexion repartira au prochain
 * `getSocket()` — c'est-à-dire quand un écran en aura réellement besoin.
 */
const MAX_UNAUTHORIZED_ATTEMPTS = 2;
let unauthorizedAttempts = 0;

/**
 * Ce refus est-il un refus d'AUTHENTIFICATION ?
 *
 * Le contrat annonce le message `unauthorized` ; les deux autres motifs
 * couvrent les formulations usuelles d'un middleware Socket.IO. Le simple mot
 * « auth » a été écarté : trop large, il aurait pris pour un jeton mort des
 * erreurs de transport sans rapport — et suspendu la reconnexion sur une
 * coupure réseau ordinaire, exactement le cas où il faut insister.
 */
function isUnauthorized(message: string): boolean {
  return /unauthorized|authentication|invalid token/i.test(message);
}

function getOrCreateSocket(): Socket {
  if (socket && socket.connected) return socket;
  if (socket) return socket; // en cours de reconnexion, on réutilise

  socket = io(env.realtimeUrl, {
    // Chemin de la passerelle, POSÉ EXPLICITEMENT.
    //
    // C'est déjà le défaut de socket.io-client, donc rien ne change
    // aujourd'hui. Mais le contrat le nomme (« chemin `/socket.io` ») et un
    // défaut de bibliothèque n'est pas un engagement : le jour où il change, la
    // panne serait totale et illisible — le handshake partirait vers une URL
    // que la passerelle ne sert pas.
    path:                 "/socket.io",

    // ── R1 · Ouvrir avec les identifiants ────────────────────────────────────
    // ⚠️ INDISPENSABLE : l'ingress répartit sur deux instances et tient
    // l'affinité par un cookie (`sos-rt-sticky`, SameSite=None). L'app vit sur
    // `mon.alomain.com`, la passerelle sur `rt.alomain.com` : la requête est
    // INTER-SITE. Sans `withCredentials`, le cookie n'est pas envoyé, la
    // requête suivante tombe sur l'autre instance, et le serveur répond
    // `Session ID unknown`. Le symptôme est une connexion qui s'établit puis se
    // coupe sans raison lisible.
    withCredentials:      true,

    // ⚠️ `transports` n'est VOLONTAIREMENT pas surchargé.
    //
    // Le code précédent imposait `["polling", "websocket"]` en pensant garantir
    // que le handshake reçoive le cookie collant. C'est déjà l'ordre par défaut
    // — la contrainte était donc sans effet — et la liste écrasait le défaut
    // réel de socket.io 4.8, qui vaut `["polling", "websocket", "webtransport"]` :
    // on retirait WebTransport sans le vouloir. Le contrat tranche : « Ne
    // surchargez pas `transports` : le défaut — sondage puis bascule — est celui
    // pour lequel l'affinité est réglée. »

    // ⚠️ R3 · `pingInterval` et `pingTimeout` ne doivent PAS être définis.
    // Le serveur impose 10 s d'intervalle et 8 de patience, contre 25 et 20 par
    // défaut. Aux valeurs par défaut, une Main dont le réseau tombait restait
    // considérée disponible trois quarts de minute : on lui proposait des
    // courses qu'elle ne voyait pas, et la cascade s'épuisait sur des Mains
    // absentes. C'est le SERVEUR qui décide — toute valeur posée ici la casse.

    autoConnect:          true,
    reconnection:         true,
    reconnectionAttempts: Infinity,
    reconnectionDelay:    1_000,
    reconnectionDelayMax: 10_000,
    // Sans ce délai, le handshake abandonne au bout de 20 s par défaut. Sur un
    // réseau mobile lent (3G béninoise), la poignée de main initiale dépasse
    // régulièrement ce seuil → `connect_error` → bannière « connexion perdue »
    // alors que le serveur répond parfaitement.
    timeout: 30_000,

    // ── R2 · Le jeton dans la poignée de main ────────────────────────────────
    // Dans `auth.token`. Pas en paramètre d'URL, pas en en-tête.
    //
    // Le navigateur ne permet pas de poser un en-tête sur le transport
    // WebSocket : un `Authorization` ne survivrait qu'au sondage et la bascule
    // échouerait. Un jeton en paramètre d'URL se retrouverait, lui, en clair
    // dans les journaux d'accès pour des semaines.
    //
    // L'ancien code envoyait AUSSI `authorization: "Bearer <jwt>"` « pour
    // couvrir les deux implémentations ». Le contrat nomme une seule clé ;
    // dupliquer le jeton dans la poignée de main n'apportait rien et le
    // recopiait dans un champ qui ne survit pas à la bascule.
    auth: (cb) => {
      // Appelée à chaque (re)connexion. Le jeton doit être FRAIS À CET INSTANT :
      // une reconnexion survient souvent après une coupure longue (veille,
      // tunnel), c'est-à-dire précisément quand l'accès a eu le temps d'expirer.
      void freshAccessToken(unauthorizedAttempts > 0).then((token) => {
        cb({ token: token ?? "" });
      });
    },
  });

  socket.on("connect", () => {
    if (env.isDev) console.log("[realtime] connecté", socket?.id);
    unauthorizedAttempts = 0;
    notifyConnection(true);

    // Repli si le serveur n'émet pas `ready` (version antérieure au protocole
    // de reprise) : sans lui, les salles ne seraient JAMAIS rejointes et l'écran
    // resterait muet après une reconnexion. Annulé dès que `ready` arrive.
    readyFallback = setTimeout(() => {
      if (env.isDev) console.warn("[realtime] pas de `ready` reçu — rejoin de secours");
      rejoinRooms();
    }, READY_GRACE_MS);
  });

  // ── R4/R5 · Attendre `ready`, et tout refaire s'il dit `recovered: false` ──
  //
  // Rejoindre avant `ready` n'est pas une faute technique, mais on ne saurait
  // pas si l'on DEVAIT rejoindre. C'est `ready` qui tranche :
  //
  //   recovered = true  → le serveur a restitué les salles et rejoué les
  //                       événements manqués : ne rien refaire, et surtout ne
  //                       pas recharger l'état pour rien.
  //   recovered = false → session neuve, n'appartenant à AUCUNE salle. C'est la
  //                       clause qui coûte le plus cher quand on l'ignore,
  //                       parce que son absence ne se voit pas : un client qui
  //                       ne réémet pas ses entrées est CONNECTÉ et n'entend
  //                       plus rien, tous les voyants au vert.
  //
  // Le serveur garde deux minutes d'événements par session. Au-delà, l'état a
  // trop changé pour qu'un rejeu ait un sens. C'est un filet pour le cas
  // fréquent, PAS une garantie de remise : la garantie, c'est qu'on relise
  // l'état quand `recovered` vaut faux.
  socket.on("ready", (payload?: ReadyPayload) => {
    if (readyFallback) {
      clearTimeout(readyFallback);
      readyFallback = null;
    }
    const recovered = Boolean(payload?.recovered);
    if (env.isDev) {
      console.log("[realtime] ready — recovered:", recovered, "salles:", payload?.rooms);
    }

    if (recovered) return;

    rejoinRooms();
    for (const fn of resyncListeners) fn();
  });

  socket.on("disconnect", (reason) => {
    if (env.isDev) console.log("[realtime] déconnecté:", reason);
    // La socket suivante sera NEUVE : aucune salle n'est acquise tant que le
    // serveur ne l'a pas confirmé. Marquer l'inverse ferait croire à un suivi
    // qui n'existe plus.
    for (const [key, state] of rooms.entries()) {
      if (state.status === "joined") setRoomStatus(key, "pending");
    }
    notifyConnection(false);
  });

  socket.on("connect_error", (err) => {
    if (env.isDev) console.warn("[realtime] connect_error:", err.message);
    notifyConnection(false);

    if (!isUnauthorized(err.message)) return;

    unauthorizedAttempts += 1;
    if (unauthorizedAttempts > MAX_UNAUTHORIZED_ATTEMPTS) {
      // On ne boucle pas : le jeton est révoqué ou la session est morte.
      // `getSocket()` repartira de zéro quand un écran en aura besoin.
      if (env.isDev) {
        console.warn("[realtime] jeton refusé de façon répétée — reconnexion suspendue");
      }
      socket?.disconnect();
      return;
    }
    // Une seule reprise : on force le rafraîchissement, la tentative suivante
    // (pilotée par socket.io) repartira avec le jeton neuf via `auth`.
    void refreshSession();
  });

  return socket;
}

export function getSocket(): Socket {
  return getOrCreateSocket();
}

/** Force une déconnexion (déconnexion utilisateur, changement de compte). */
export function disconnectSocket(): void {
  // Purge INCONDITIONNELLE : on change de compte, les références des écrans
  // encore montés n'ont plus lieu d'être.
  for (const state of rooms.values()) {
    if (state.retry) clearTimeout(state.retry);
  }
  rooms.clear();
  unauthorizedAttempts = 0;
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  notifyConnection(false);
}

// ─── R10 · N'émettre que des entrées en salle ────────────────────────────────
// La passerelle ne rediffuse JAMAIS un événement reçu d'un client, et le corps
// d'un message est plafonné à 16 Ko : au-delà, la connexion est coupée. Ce
// module n'émet que `join.trip`, `join.order` et `join.agent`, dont les charges
// utiles tiennent en quelques dizaines d'octets. Aucun autre `emit` ne doit
// être ajouté ici, ni ailleurs dans l'app.

// ─── Souscriptions / désabonnements ──────────────────────────────────────────
// Retourne une fonction de nettoyage, appelée par les hooks React au démontage.

export function subscribeOrder(
  orderId: string,
  handler: (payload: OrderUpdatedPayload) => void,
): () => void {
  const s   = getOrCreateSocket();
  const { key, shouldJoin } = addRoom("order", orderId);
  if (shouldJoin) joinRoom(key);
  s.on("order.updated", handler);

  return () => {
    s.off("order.updated", handler);
    removeRoom(key);
  };
}

/**
 * Offres de course adressées à la Main connectée — `trip.agent.offered`.
 *
 * ## Aucune salle à rejoindre
 * Dès la poignée de main, la passerelle inscrit la socket dans **`user:{sub}`**,
 * d'après le claim `sub` du jeton. Il n'y a RIEN à émettre — et rien à quitter :
 * cette salle revient d'elle-même après une reconnexion, contrairement aux
 * `trip:{id}` qu'il faut réémettre.
 *
 * ## Ce que l'offre ne contient pas
 * Aucune donnée métier : ni adresse, ni montant, ni nom. Ce n'est qu'un RÉVEIL.
 * L'écran d'offre exige toujours un `GET /trips/{trip_id}` derrière, et la
 * liste reste la seule source de vérité.
 *
 * ## Deux gardes, et elles servent
 * **Doublons.** L'outbox n'a aucune garantie d'exactement-une-fois : un échec en
 * cours de traitement laisse l'événement non publié, et il repart cinq secondes
 * plus tard. On désambiguïse donc par `trip_id` + `expires_at` — deux réceptions
 * de la MÊME offre ne réveillent l'app qu'une fois.
 *
 * **Échéance.** Une offre sans `expires_at` exploitable est ignorée : rien ne
 * permettrait d'afficher un compte à rebours honnête, ni de savoir si elle est
 * encore ouverte.
 */
export function subscribeOffers(
  handler: (offer: TripOfferPayload) => void,
): () => void {
  const s = getOrCreateSocket();

  // Fenêtre de déduplication : une même offre rejouée par l'outbox arrive à
  // quelques secondes d'intervalle. On garde donc peu, et on nettoie.
  const seen = new Map<string, number>();
  const DEDUPE_MS = 60_000;

  const wrapped = (raw: unknown) => {
    const offer = (raw ?? {}) as Partial<TripOfferPayload>;
    if (typeof offer.trip_id !== "string" || !offer.trip_id) return;
    if (typeof offer.expires_at !== "string" || !Number.isFinite(Date.parse(offer.expires_at))) {
      if (env.isDev) console.warn("[realtime] offre ignorée — `expires_at` inexploitable", raw);
      return;
    }

    const now = Date.now();
    for (const [k, at] of seen) if (now - at > DEDUPE_MS) seen.delete(k);

    const key = `${offer.trip_id}|${offer.expires_at}`;
    if (seen.has(key)) return;
    seen.set(key, now);

    handler(offer as TripOfferPayload);
  };

  s.on("trip.agent.offered", wrapped);
  return () => {
    s.off("trip.agent.offered", wrapped);
    seen.clear();
  };
}

/**
 * L'offre nous est passée sous le nez — `trip.offer.superseded`.
 *
 * §1 du contrat du 2026-09-02, et c'est une RUPTURE : la même course part
 * désormais à TROIS Mains simultanément, et la première qui accepte l'emporte.
 * Avant, elle partait à une seule, qui avait cinq minutes pendant que les
 * autres ne savaient rien.
 *
 * ## Pourquoi l'écouter n'est pas facultatif
 * Sans cet événement, la feuille d'offre reste ouverte sur une course déjà
 * partie. La Main la lit, l'accepte, reçoit un `409`, et découvre l'échec au
 * moment où elle croyait avoir gagné. Pire si elle a commencé à rouler : le
 * contrat le dit sans détour — « rien n'est plus pénible que de rouler vers une
 * course déjà partie ».
 *
 * ## Salle personnelle — rien à rejoindre
 * Comme `trip.agent.offered`, il arrive dans `user:{sub}`, où la passerelle
 * nous inscrit dès la poignée de main.
 */
export type OfferSupersededRaison = "prise_par_une_autre_main" | "course_annulee";

export type OfferSupersededPayload = {
  trip_id?: string;
  /**
   * Pourquoi l'offre tombe. Les deux valeurs connues au contrat ; on n'en
   * suppose pas la fermeture — un motif inconnu ferme quand même l'offre, ce
   * qui est le comportement sûr.
   */
  raison?:  OfferSupersededRaison | string;
};

export function subscribeOfferSuperseded(
  handler: (payload: OfferSupersededPayload) => void,
): () => void {
  const s = getOrCreateSocket();

  const wrapped = (raw: unknown) => {
    const p = (raw ?? {}) as OfferSupersededPayload;
    // Sans `trip_id`, impossible de savoir QUELLE offre fermer. Fermer la
    // courante à l'aveugle ferait disparaître une offre encore valable.
    if (typeof p.trip_id !== "string" || !p.trip_id) {
      if (env.isDev) console.warn("[realtime] offre supplantée ignorée — `trip_id` absent", raw);
      return;
    }
    handler(p);
  };

  s.on("trip.offer.superseded", wrapped);
  return () => {
    s.off("trip.offer.superseded", wrapped);
  };
}

/**
 * Versement à la boutique — `stop.payment.released` / `stop.payment.refused`.
 *
 * ## Le blocage que ceci lève
 * Entre l'accord du client sur les prix et le moment où la Main peut acheter,
 * l'argent doit parvenir au vendeur. Rien ne signalait ce moment.
 *
 * ⚠️ Sa NATURE a changé le 2026-09-04 : le versement n'est plus un geste humain
 * consigné après coup, la plateforme décaisse elle-même vers le numéro de la
 * boutique (trois secondes en production). `released` n'est donc plus émis au
 * clic d'un opérateur mais à la confirmation du prestataire — plus tard, et
 * **jamais** si le décaissement échoue.
 *
 * Ces deux événements sont le SEUL signal de ce moment. Sans eux, la Main
 * restait devant le comptoir sans rien à l'écran : ni « on vous verse », ni
 * « c'est bon, achetez », ni le motif d'un refus. Elle ne pouvait que rafraîchir
 * au hasard.
 *
 * ## Salle personnelle — rien à rejoindre
 * Comme `trip.agent.offered`, ils arrivent dans `user:{sub}`, où la passerelle
 * inscrit la socket dès la poignée de main. Aucun `emit`, et la salle revient
 * d'elle-même après une reconnexion.
 *
 * ## Pourquoi un filtre par course est laissé à l'appelant
 * Une Main ne porte qu'une course à la fois en pratique, mais rien ne
 * l'impose au contrat. L'écran filtre donc sur `trip_id` quand la charge utile
 * en porte un — même précaution que sur la salle de course.
 */
export type StopPaymentPayload = {
  trip_id?:    string;
  stop_index?: number;
  /** Montant versé à la boutique, quand le serveur le porte. */
  amount?:     number;
  /** Motif du refus — à AFFICHER tel quel : c'est l'opérateur qui l'écrit. */
  reason?:     string | null;
};

export type StopPaymentDecision = "released" | "refused";

export function subscribeStopPayments(
  handler: (decision: StopPaymentDecision, payload: StopPaymentPayload) => void,
): () => void {
  const s = getOrCreateSocket();

  const onReleased = (raw: unknown) => handler("released", (raw ?? {}) as StopPaymentPayload);
  const onRefused  = (raw: unknown) => handler("refused",  (raw ?? {}) as StopPaymentPayload);

  s.on("stop.payment.released", onReleased);
  s.on("stop.payment.refused",  onRefused);

  return () => {
    s.off("stop.payment.released", onReleased);
    s.off("stop.payment.refused",  onRefused);
  };
}

/**
 * Retraits d'une Main — `agent.payout.paid` / `.failed` / `.rejected`.
 *
 * ## Ce que ces trois événements évitent
 * Entre la demande de retrait et l'argent qui arrive, il y a une approbation
 * humaine puis un virement de prestataire. Rien, dans une réponse REST, ne dit
 * quand l'un ou l'autre aboutit : sans ces événements, une Main qui a demandé
 * son argent n'a que le rechargement au hasard pour savoir où il en est.
 *
 * ## La phrase à ne surtout pas réécrire
 * Sur `failed` et `rejected`, la charge utile porte `consequence`, qui dit
 * explicitement que les gains restent disponibles. **À afficher tel quel.**
 * C'est le seul rempart contre la conclusion qu'une Main tire naturellement
 * d'un échec de virement — « j'ai perdu mon argent » — et cette conclusion-là
 * se termine toujours par un appel au support.
 *
 * ## Salle personnelle — rien à rejoindre
 * Comme `trip.agent.offered` et les versements boutique, ils arrivent dans
 * `user:{sub}`, où la passerelle inscrit la socket dès la poignée de main.
 * Aucun `emit`, et la salle revient d'elle-même après une reconnexion.
 *
 * ⚠️ Ne PAS les ajouter à `TRIP_ROOM_EVENTS` : ils n'ont pas de `trip_id`, et le
 * relais de `subscribeTrip` les filtrerait de toute façon.
 *
 * ## À la réception, recharger
 * Les trois changent le solde. Le contrat est net : « À la réception de l'un des
 * trois, rechargez `GET /agent-earnings` ». Cf. `useAgentPayoutUpdates`.
 */
export type AgentPayoutOutcome = "paid" | "failed" | "rejected";

export type AgentPayoutPayload = {
  payout_id?:  string;
  /** Ce qui avait été demandé. */
  amount?:     number;
  /** Ce qui a réellement été viré, frais déduits — porté par `paid`. */
  net_amount?: number;
  /** Le numéro crédité — porté par `paid`. */
  phone?:      string;
  /** Motif d'un `failed` : le virement n'a pas abouti. */
  raison?:     string | null;
  /** Motif d'un `rejected` : un opérateur a tranché. */
  motif?:      string | null;
  /**
   * Ce que ça change pour la Main — « vos gains restent disponibles ».
   * Porté par `failed` et `rejected`. À AFFICHER TEL QUEL.
   */
  consequence?: string | null;
};

export function subscribeAgentPayouts(
  handler: (outcome: AgentPayoutOutcome, payload: AgentPayoutPayload) => void,
): () => void {
  const s = getOrCreateSocket();

  // Trois références NOMMÉES, et non des flèches en ligne : `s.off` exige la
  // même référence que `s.on`, sinon le désabonnement ne retire rien et les
  // écouteurs s'empilent à chaque remontage de l'écran.
  const onPaid     = (raw: unknown) => handler("paid",     (raw ?? {}) as AgentPayoutPayload);
  const onFailed   = (raw: unknown) => handler("failed",   (raw ?? {}) as AgentPayoutPayload);
  const onRejected = (raw: unknown) => handler("rejected", (raw ?? {}) as AgentPayoutPayload);

  s.on("agent.payout.paid",     onPaid);
  s.on("agent.payout.failed",   onFailed);
  s.on("agent.payout.rejected", onRejected);

  return () => {
    s.off("agent.payout.paid",     onPaid);
    s.off("agent.payout.failed",   onFailed);
    s.off("agent.payout.rejected", onRejected);
  };
}

/**
 * Salle d'un AGENT (`join.agent`).
 *
 * ⚠️ R7/R9 — À N'UTILISER QUE PAR L'AGENT LUI-MÊME OU UN SUPERVISEUR.
 *
 * Pour SUIVRE une course, `trip:{id}` suffit : tout y arrive, position de la
 * Main comprise depuis le 2026-08-26. Le contrat est explicite — « Vous n'avez
 * plus besoin de `join.agent` ».
 *
 * Et y rester coûte cher : on continue de recevoir la position de cette personne
 * APRÈS la fin de la course, sur ses autres missions. En passant par la salle de
 * la course, la question ne se pose pas : elle se vide d'elle-même quand la
 * course se termine.
 */
export function subscribeAgent(
  agentId: string,
  handler: (payload: AgentLocationUpdatedPayload) => void,
): () => void {
  const s   = getOrCreateSocket();
  const { key, shouldJoin } = addRoom("agent", agentId);
  if (shouldJoin) joinRoom(key);

  const wrapped = (payload: AgentLocationUpdatedPayload) => {
    // ⚠️ FILTRE ESSENTIEL : `agent.location.updated` est un événement de socket,
    // pas de salle — tout ce que le serveur diffuse arrive ici, y compris les
    // positions d'AUTRES agents (plusieurs salles jointes après une
    // reconnexion). Sans ce test, la moto du client sautait sur la position
    // d'un autre livreur, à des kilomètres, puis revenait.
    if (payload.agent_id !== agentId) return;

    // Coordonnées invalides (null, 0/0, hors bornes) : une valeur 0/0 est le
    // « point zéro » au large de l'Afrique — elle projetterait la moto en plein
    // golfe de Guinée. Mieux vaut garder la dernière position connue.
    if (!isUsableCoordinate(payload.latitude, payload.longitude)) return;

    handler(payload);
  };
  s.on("agent.location.updated", wrapped);

  return () => {
    s.off("agent.location.updated", wrapped);
    removeRoom(key);
  };
}

/**
 * Salle d'une COURSE (`join.trip`) — le chemin NORMAL du suivi (R7).
 *
 * Tout y arrive : changements d'état, offres, expirations, abandons, demandes
 * de validation… et la position de la Main (R8).
 *
 * ## Ce que R8 corrige
 * La position partait uniquement vers `agent:{id}`, la salle prévue pour cela —
 * mais il faut y entrer, et l'application ne l'a jamais demandé. Elle est
 * désormais portée AUSSI dans `trip:{id}`, là où le client se trouve déjà.
 * Diffuser deux fois ne coûte presque rien : une salle sans auditeur n'émet vers
 * personne.
 *
 * Un second défaut a été corrigé au passage : le client n'était admis dans
 * `agent:{id}` qu'à partir de `AGENT_EN_ROUTE`, or la carte s'ouvre à
 * l'affectation, demande la salle, et ne redemande jamais.
 *
 * `onAnyEvent` reçoit TOUT événement de course, y compris ceux que cette
 * version de l'app ne connaît pas — le contrat interdit l'aiguillage exhaustif
 * qui casserait sur un nom inconnu.
 */
export function subscribeTrip(
  tripId: string,
  handlers: {
    onUpdate?:          (payload: unknown) => void;
    onAgentLocation?:   (payload: AgentLocationUpdatedPayload) => void;
    onAwaitingApproval?: (payload: TripAwaitingApprovalPayload) => void;
    /** Tout événement de la salle, connu ou non. Reçoit aussi ceux ci-dessus. */
    onAnyEvent?:        (event: string, payload: unknown) => void;
  },
): () => void {
  const s   = getOrCreateSocket();
  const { key, shouldJoin } = addRoom("trip", tripId);
  if (shouldJoin) joinRoom(key);

  const onAgentLocation = handlers.onAgentLocation
    ? (payload: AgentLocationUpdatedPayload) => {
        // Même filtre de validité que la salle agent : une coordonnée 0/0
        // projetterait la moto au large du golfe de Guinée.
        if (!isUsableCoordinate(payload.latitude, payload.longitude)) return;
        handlers.onAgentLocation?.(payload);
      }
    : undefined;

  // Un écouteur par événement CONNU. Les noms que cette version ignore sont
  // absorbés par le relais `onAny` posé plus bas — et non par cette boucle, qui
  // ne peut par construction voir que ce qu'elle a déjà nommé.
  const bound: [string, (payload: unknown) => void][] = [];

  for (const event of TRIP_ROOM_EVENTS) {
    const fn = (payload: unknown) => {
      // ⚠️ Socket.IO ne cloisonne PAS les événements par salle CÔTÉ CLIENT :
      // `s.on("trip.updated")` reçoit tout ce que le socket voit, y compris les
      // courses d'autres salles jointes en parallèle. Sans ce filtre, l'écran
      // d'une course réagissait aux événements d'une autre — le même défaut que
      // celui déjà corrigé sur `agent.location.updated`.
      //
      // On ne filtre que si la charge utile porte un `trip_id` : un événement
      // qui n'en a pas ne peut pas être attribué, et le laisser passer vaut
      // mieux que le perdre.
      const carried = (payload as { trip_id?: unknown } | null)?.trip_id;
      if (typeof carried === "string" && carried !== tripId) return;

      handlers.onAnyEvent?.(event, payload);
      if (event === "trip.updated" || event === "trip.status.changed") {
        handlers.onUpdate?.(payload);
      }
      if ((AWAITING_APPROVAL_EVENTS as readonly string[]).includes(event)) {
        handlers.onAwaitingApproval?.((payload ?? {}) as TripAwaitingApprovalPayload);
      }
    };
    bound.push([event, fn]);
    s.on(event, fn);
  }

  if (onAgentLocation) {
    s.on("agent.location.updated", onAgentLocation as (payload: unknown) => void);
  }

  // ─── LE VRAI relais générique ──────────────────────────────────────────────
  //
  // ⚠️ Il n'en existait pas. Le commentaire ci-dessus promettait « un relais
  // générique qui absorbe les noms que cette version ignore », mais
  // `onAnyEvent` n'était appelé QUE depuis la boucle sur `TRIP_ROOM_EVENTS` :
  // un événement absent de la liste n'atteignait strictement personne.
  //
  // Ce que ça coûtait, vu en production le 2026-09-06 : la liste porte
  // `trip.awaiting.client.approval` — l'ENTRÉE en attente de validation — mais
  // AUCUN nom pour la RÉPONSE du client. La Main restait donc sur « En attente
  // de la validation du client… » alors que le client avait validé, et ne
  // l'apprenait qu'en rechargeant sa page. Debout devant le comptoir.
  //
  // `onAny` supprime la classe entière de défauts : tout événement attribuable
  // à CETTE course rafraîchit l'écran, que cette version du front connaisse son
  // nom ou non. C'est la propriété qu'il fallait — le serveur peut nommer ses
  // événements comme il veut, l'app suit.
  //
  // Un `trip_id` STRICTEMENT égal est exigé ici, là où les noms connus tolèrent
  // son absence : un événement inconnu et non attribuable peut tout aussi bien
  // venir de la salle personnelle (une offre, un retrait), et déclencher une
  // relecture de course sur chacun serait du bruit pur.
  const knownEvents = new Set<string>(TRIP_ROOM_EVENTS);
  const onAnyRelay = (event: string, payload: unknown) => {
    if (knownEvents.has(event)) return; // déjà traité par son écouteur dédié
    const carried = (payload as { trip_id?: unknown } | null)?.trip_id;
    if (carried !== tripId) return;

    handlers.onAnyEvent?.(event, payload);
    // Un événement de cette course veut dire « va relire » : on ne sait pas ce
    // qu'il annonce, on sait qu'il annonce quelque chose.
    handlers.onUpdate?.(payload);
  };
  s.onAny(onAnyRelay);

  return () => {
    for (const [event, fn] of bound) s.off(event, fn);
    if (onAgentLocation) {
      s.off("agent.location.updated", onAgentLocation as (payload: unknown) => void);
    }
    s.offAny(onAnyRelay);
    removeRoom(key);
  };
}
